import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { CalendarCheck, Play, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const money = (v) => `£${Number(v || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => (iso ? new Date(iso + (iso.length === 10 ? "T00:00:00" : "")).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" }) : "—");
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Europe/London" }) : "—");
const ordinal = (n) => { const s = ["th", "st", "nd", "rd"], v = n % 100; return n + (s[(v - 20) % 10] || s[v] || s[0]); };
const selectCls = "h-8 rounded-md border border-input bg-background px-2 text-xs";

/* Main client: schedule a weekly / monthly DRAFT consolidated invoice covering every sub-client job, at a set UK time. */
export default function AutoConsolidatePanel({ client }) {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  const load = () => API.get(`/clients/${client.id}/auto-consolidate`).then(({ data }) => { setStatus(data); setForm(data.settings); }).catch(() => {});
  useEffect(() => { load(); }, [client.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (patch) => {
    const next = { ...form, ...patch };
    setForm(next);
    setSaving(true);
    try {
      await API.put(`/clients/${client.id}`, { auto_consolidate: next });
      await load();
      if ("enabled" in patch) toast.success(patch.enabled ? "Auto-consolidate switched on — drafts will be created on schedule" : "Auto-consolidate switched off");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save schedule"); }
    finally { setSaving(false); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await API.post(`/clients/${client.id}/auto-consolidate/run`);
      if (data.status === "created") toast.success(`${data.reference} drafted — ${data.lines} line${data.lines === 1 ? "" : "s"} for ${data.period_start} to ${data.period_end}${data.missing_po ? ` · ${data.missing_po} missing PO/WO` : ""}`);
      else if (data.status === "exists") toast.info(`${data.reference} already covers ${data.period_start} to ${data.period_end}`);
      else if (data.status === "nothing_to_bill") toast.info(`Nothing un-invoiced for ${data.period_start} to ${data.period_end}`);
      else toast.info(data.status);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Run failed"); }
    finally { setRunning(false); }
  };

  if (!form) return null;
  const weekly = form.frequency === "weekly";
  const hh = `${String(form.hour ?? 0).padStart(2, "0")}:00`;

  return (
    <div className="border-t border-border" data-testid="auto-consolidate-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 p-4 bg-violet-500/5">
        <div className="flex items-center gap-3 min-w-0">
          <CalendarCheck size={20} className="text-violet-600 shrink-0" />
          <div className="min-w-0">
            <h3 className="text-sm font-semibold font-[Chivo]">Auto-consolidate</h3>
            <p className="text-[10px] text-muted-foreground mt-0.5">Automatically draft one consolidated invoice for every completed sub-client job — {weekly ? "each week for the previous Mon–Sun" : "each month for the previous calendar month"}. Drafts are never sent without review.</p>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="text-xs text-muted-foreground" data-testid="auto-consolidate-state">{form.enabled ? "On" : "Off"}</span>
          <Switch checked={!!form.enabled} disabled={saving} onCheckedChange={(v) => save({ enabled: v })} data-testid="auto-consolidate-toggle" />
        </div>
      </div>

      <div className="px-4 pb-4 space-y-3">
        <div className="flex flex-wrap items-end gap-3 text-xs">
          <label className="flex flex-col gap-1">Frequency
            <select value={form.frequency} onChange={(e) => save({ frequency: e.target.value, day: e.target.value === "weekly" ? 0 : 1 })} className={selectCls} data-testid="auto-consolidate-frequency">
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </label>
          <label className="flex flex-col gap-1">{weekly ? "On" : "On day"}
            <select value={form.day} onChange={(e) => save({ day: Number(e.target.value) })} className={selectCls} data-testid="auto-consolidate-day">
              {weekly ? WEEKDAYS.map((w, i) => <option key={i} value={i}>{w}</option>) : Array.from({ length: 28 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{ordinal(d)}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1">At (UK time)
            <select value={form.hour ?? 0} onChange={(e) => save({ hour: Number(e.target.value) })} className={selectCls} data-testid="auto-consolidate-hour">
              {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1">Our mark-up A %
            <Input type="number" step="0.5" value={form.markup_a_pct} onChange={(e) => setForm({ ...form, markup_a_pct: e.target.value })} onBlur={() => save({})} className="h-8 w-20 text-xs" data-testid="auto-consolidate-markup-a" />
          </label>
          <label className="flex flex-col gap-1">Client mark-up B %
            <Input type="number" step="0.5" value={form.markup_b_pct} onChange={(e) => setForm({ ...form, markup_b_pct: e.target.value })} onBlur={() => save({})} className="h-8 w-20 text-xs" data-testid="auto-consolidate-markup-b" />
          </label>
          <label className="flex flex-col gap-1">Due days
            <Input type="number" min="1" value={form.due_days} onChange={(e) => setForm({ ...form, due_days: e.target.value })} onBlur={() => save({})} className="h-8 w-16 text-xs" data-testid="auto-consolidate-due-days" />
          </label>
          <label className="flex items-center gap-1.5 h-8 cursor-pointer"><input type="checkbox" checked={!!form.include_main} onChange={(e) => save({ include_main: e.target.checked })} data-testid="auto-consolidate-include-main" /> Include {client.company_name || "the main client"}'s own jobs</label>
          <label className="flex items-center gap-1.5 h-8 cursor-pointer"><input type="checkbox" checked={!!form.vat_enabled} onChange={(e) => save({ vat_enabled: e.target.checked })} data-testid="auto-consolidate-vat" /> Add VAT</label>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-muted/20 px-3 py-2 text-xs">
          <p data-testid="auto-consolidate-next">
            {form.enabled ? <><span className="font-semibold">Next run:</span> {fmtWhen(status?.next_run)}</> : <><span className="font-semibold">Schedule:</span> {weekly ? `every ${WEEKDAYS[form.day]}` : `the ${ordinal(form.day)} of each month`} at {hh} (UK)</>}
            <span className="text-muted-foreground"> · covers {fmtDate(status?.next_period_start)} → {fmtDate(status?.next_period_end)}</span>
          </p>
          <Button size="sm" variant="outline" className="h-7 text-xs" disabled={running} onClick={runNow} data-testid="auto-consolidate-run-btn"><Play size={12} className="mr-1" weight="fill" /> {running ? "Running…" : "Run now for the last period"}</Button>
        </div>

        {status?.recent?.length > 0 && (
          <div data-testid="auto-consolidate-recent">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Recent auto-drafts</p>
            <ul className="divide-y divide-border border border-border rounded-md">
              {status.recent.map((inv) => (
                <li key={inv.id}>
                  <Link to={`/invoices/${inv.id}`} className="flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-muted/40" data-testid={`auto-consolidate-recent-${inv.id}`}>
                    <span className="font-mono text-muted-foreground">{inv.reference}</span>
                    <span className="flex-1 truncate">{inv.consolidated?.period_start} → {inv.consolidated?.period_end} · {inv.consolidated?.job_ids?.length + (inv.consolidated?.emergency_ids?.length || 0)} line{(inv.consolidated?.job_ids?.length + (inv.consolidated?.emergency_ids?.length || 0)) === 1 ? "" : "s"}</span>
                    {inv.consolidated?.missing_po > 0 && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-700">{inv.consolidated.missing_po} missing PO/WO</span>}
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold uppercase bg-muted">{inv.status}</span>
                    <span className="font-semibold">{money(inv.total_inc_vat || inv.grand_total)}</span>
                    <ArrowRight size={12} className="text-muted-foreground" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
